/**
 * scheduleEmployeeBars.ts
 * Frontend service for the Employee Schedule UX overhaul (M0 endpoints).
 *
 * GET   /api/schedule/employee-bars
 * PATCH /api/schedule/employee-assignments/:key/reassign
 * POST  /api/schedule/employee-assignments/:key/span
 * GET   /api/schedule/employee-assignments/:key/reassign-candidates
 * POST  /api/schedule/presence/heartbeat   GET /api/schedule/presence
 *
 * 409 skill_gate / absence_day_conflict are re-thrown as the shared
 * SkillGateError / AbsenceDayConflictError. The absence conflict is HARD (Q3B):
 * no confirm path — callers show the server message verbatim.
 */

import { AxiosError } from 'axios';
import apiClient from './apiClient';
import {
  type WeekOverloadAdvisory,
  SkillGateError,
  toAbsenceDayConflictError,
  toScenarioLockedError,
  type SkillGateBlockedUser,
} from './scheduleAssignments';

export type EmployeeBarKind = 'Task' | 'Vacation' | 'Sick' | 'FieldWork' | 'Other';

export interface EmployeeBarUser {
  userKey: string;
  displayName: string;
  email: string;
  jobClass: string | null;
  tier: string | null;
  workGroup: string | null;
  photoUrl: string | null;
}

export interface EmployeeBar {
  assignmentKey: string;
  userKey: string;
  name: string;
  kind: EmployeeBarKind;
  weekStart: string;
  startDate: string;
  endDate: string;
  /**
   * Task bars: the person's count-once allocation share of this bar's job for
   * the week (EmployeeSourceAllocation %, BUG-2026-0196) — 100 when no
   * allocation row exists (presence fallback). Dated absences: 100.
   */
  percent: number;
  /** Stored EmployeeAssignment.Percent (pinned 100 on presence rows — historical). */
  rawPercent: number;
  status: 'Tentative' | 'Committed';
  source: 'Quote' | 'WorkOrder' | 'Manual';
  scenarioKey: string;
  scheduleTaskKey: string | null;
  taskTypeKey: string | null;
  quoteKey: string | null;
  quoteNumber: string | null;
  workOrderKey: string | null;
  workOrderNo: string | null;
  /** Descriptive WorkOrder name — bar label (falls back to projectName/name). */
  workOrderName: string | null;
  projectKey: string | null;
  projectId: string | null;
  /** Descriptive Project name — bar-label fallback when WorkOrderName is blank. */
  projectName: string | null;
  machineBookingKey: string | null;
  machineCode: string | null;
  dayLocked: boolean;
  /**
   * RM-4 — the task's RunMode snapshot ('attended' | 'continuous'). A
   * continuous row is working-day attendance of a 24/7 test that also runs
   * through weekends; the bars/drawer say so. Optional — older payloads omit it.
   */
  runMode?: 'attended' | 'continuous';
  actingUserSkillLevel: number | null;
  conflictType: 'hard' | 'soft' | null;
  /** Wave 3 (Q3B): true when this bar's week has a task-on-absence-day CONFLICT. */
  overbooked: boolean;
}

export interface EmployeeWeekCapacityCell {
  userKey: string;
  weekStart: string;
  /** LEGACY-scale weekly sum (busy day = 100, conflict day = 200). Prefer the
   *  additive busyDays/workingDays below for the Q5B percent-of-week figure. */
  totalPercent: number;
  /** Wave 3 (Q3B): true only for a task-on-absence-day CONFLICT in the week. */
  hasOverbook: boolean;
  /** Wave-3 additive: count of busy working days (Q5B numerator). */
  busyDays?: number;
  /** Wave-3 additive: working days in the week (holiday-aware denominator). */
  workingDays?: number;
  /** Wave-3 additive: working day → task count (0-count days included — Q1B chip). */
  byDayTaskCount?: Record<string, number>;
  /** Wave-3 additive: working day → covered by a dated absence. */
  byDayAbsent?: Record<string, boolean>;
  /**
   * Staffing Allocation % (PR C / D6) — Σ intentional allocation % over the
   * DISTINCT WO/quote sources this person is present in that week (presence
   * fallback 100). The Employee Schedule matrix renders this as an under/
   * healthy/at/over utilization band. Optional (stale backend → band hidden).
   */
  allocationPercent?: number;
}

export interface EmployeeBarsResponse {
  windowStart: string;
  windowEnd: string;
  weekStarts: string[];
  users: EmployeeBarUser[];
  bars: EmployeeBar[];
  capacity: EmployeeWeekCapacityCell[];
}

/** Raw EmployeeAssignment row returned by mutation endpoints (PascalCase columns). */
export interface RawAssignmentRow {
  EmployeeAssignmentKey: string;
  UserKey: string;
  WeekStart: string;
  ScheduleTaskKey: string | null;
  AbsenceType: string | null;
  Percent: number;
  Status: string;
  Source: string;
  ScenarioKey: string;
}

export interface ReassignResult {
  removed: string | null;
  created: RawAssignmentRow;
  mentorCreated: RawAssignmentRow | null;
  /** G2-7 additive: non-blocking weekly-overload advisories for this write. */
  weekOverloaded?: WeekOverloadAdvisory[];
}

export interface SpanResult {
  added: RawAssignmentRow[];
  removed: string[];
  kept: string[];
  /** G2-7 additive: non-blocking weekly-overload advisories for this write. */
  weekOverloaded?: WeekOverloadAdvisory[];
}

export interface ReassignCandidate {
  userKey: string;
  displayName: string;
  photoUrl: string | null;
  tier: string | null;
  skillLevel: number | null;
  weekTotalPercent: number;
  /** Wave 3 (Q5B): percent of the week still FREE — (workingDays − busyDays)
   *  ÷ workingDays × 100. Free DAYS, not a percent-point budget. */
  freeCapacity: number;
  /** Wave 3 (Q3B): false when the move would land a task on the person's day off. */
  fitsWithoutOverbook: boolean;
  skillOk: boolean;
  needsMentor: boolean;
}

export interface ReassignCandidatesResponse {
  taskTypeKey: string | null;
  weekStart: string;
  candidates: ReassignCandidate[];
}

export interface PresenceViewer {
  userKey: string;
  displayName: string;
  weekStart?: string;
  lastSeenMs: number;
}

export type ScheduleView = 'employee-schedule' | 'weekly-board' | 'resource-timeline';

// ── reads ─────────────────────────────────────────────────────────────────
export async function fetchEmployeeBars(startDate: string, endDate: string): Promise<EmployeeBarsResponse> {
  const params = new URLSearchParams({ startDate, endDate });
  const res = await apiClient.get<EmployeeBarsResponse>(`/schedule/employee-bars?${params.toString()}`);
  return res.data;
}

export async function fetchReassignCandidates(assignmentKey: string): Promise<ReassignCandidatesResponse> {
  const res = await apiClient.get<ReassignCandidatesResponse>(
    `/schedule/employee-assignments/${assignmentKey}/reassign-candidates`,
  );
  return res.data;
}

// ── mutations ───────────────────────────────────────────────────────────────
/** Map a 409 scenario_locked / skill_gate / absence_day_conflict into the shared typed errors; rethrow others. */
function rethrowConstraint(err: unknown): never {
  const locked = toScenarioLockedError(err); // P2-8: human message + remedy
  if (locked) throw locked;
  const absence = toAbsenceDayConflictError(err);
  if (absence) throw absence;
  const ax = err as AxiosError<{ error?: string; message?: string; blockedUsers?: SkillGateBlockedUser[]; taskTypeKey?: string | null }>;
  const data = ax.response?.data;
  if (ax.response?.status === 409 && data?.error === 'skill_gate') {
    throw new SkillGateError(data.message ?? 'Skill gate', data.blockedUsers ?? [], data.taskTypeKey ?? null);
  }
  throw err;
}

export async function reassignAssignment(
  assignmentKey: string,
  opts: {
    newUserKey: string;
    mentorUserKey?: string | null;
    /** W2-8 (P-15): "Assign anyway" — bypass the advisory per-week mentor gate. */
    confirmSkillGate?: boolean;
  },
): Promise<ReassignResult> {
  try {
    const res = await apiClient.patch<ReassignResult>(
      `/schedule/employee-assignments/${assignmentKey}/reassign`,
      opts,
    );
    return res.data;
  } catch (err) {
    rethrowConstraint(err);
  }
}

export async function spanAssignment(
  assignmentKey: string,
  opts: { startWeek: string; endWeek: string },
): Promise<SpanResult> {
  try {
    const res = await apiClient.post<SpanResult>(
      `/schedule/employee-assignments/${assignmentKey}/span`,
      opts,
    );
    return res.data;
  } catch (err) {
    rethrowConstraint(err);
  }
}

// ── presence ──────────────────────────────────────────────────────────────
export async function postPresenceHeartbeat(view: ScheduleView, weekStart?: string): Promise<void> {
  await apiClient.post('/schedule/presence/heartbeat', { view, weekStart });
}

export async function fetchPresence(view: ScheduleView): Promise<PresenceViewer[]> {
  const res = await apiClient.get<{ viewers: PresenceViewer[] }>(
    `/schedule/presence?view=${encodeURIComponent(view)}`,
  );
  return res.data.viewers;
}
